import { FC, ReactNode } from 'react';
import { FormattedMessage } from 'dibs-react-intl';

import { Link } from 'dibs-elements/exports/Link';
import DeniedPaymentAuthorizationMessage from 'dibs-error-tools/exports/ErrorMessages/DeniedPaymentAuthorizationMessage';
import { DeniedPaymentAuthorizationError } from 'dibs-error-tools/exports/ErrorMessages/constants';

import { ERRORS } from '../constants/checkoutConstants';
import { DENIED_PAYMENT_ERRORS } from '../helpers/getDeniedPaymentAuthorizationError';
import { useAppDispatch } from '../reducers/root';
import { setCheckoutHelp } from '../actions/checkoutUIActions';

import styles from '../components/containers/styles/CheckoutErrorPage.scss';

// TODO separate this file into payment errors and global errors

const ContactLink: FC<{ children?: ReactNode }> = ({ children }) => {
    const dispatch = useAppDispatch();

    if (!children) {
        return null;
    }

    return (
        <Link
            className={styles.contactUsLink}
            onClick={() => dispatch(setCheckoutHelp({ showCheckoutHelp: true }))}
        >
            {children}
        </Link>
    );
};

const CheckoutErrorMessage: FC<{
    errorCode: (typeof ERRORS)[keyof typeof ERRORS] | DeniedPaymentAuthorizationError;
}> = ({ errorCode }) => {
    switch (errorCode) {
        case ERRORS.ERROR_401:
            return (
                <FormattedMessage
                    id="checkout.CheckoutErrorMessage.401"
                    defaultMessage="Your login has expired"
                />
            );
        case ERRORS.ERROR_412:
        case ERRORS.ERROR_500:
            return (
                <FormattedMessage
                    id="checkout.CheckoutErrorMessage.412"
                    defaultMessage="We apologize, but temporary system issues prevented us from processing your payment. Please try again. If the problem persists, <a>contact us</a>."
                    values={{
                        a: content => <ContactLink>{content}</ContactLink>,
                    }}
                />
            );
        case ERRORS.ERROR_409:
            return (
                <FormattedMessage
                    id="checkout.CheckoutErrorMessage.409"
                    defaultMessage="There was an error processing your transaction. Please refresh the page and try again. If the problem persists, please <a>contact us</a> for assistance."
                    values={{ a: content => <ContactLink>{content}</ContactLink> }}
                />
            );
        case ERRORS.ERROR_429:
            return (
                <FormattedMessage
                    id="checkout.CheckoutErrorMessage.429"
                    defaultMessage="Too many attempt failures, please try again later or contact customer support."
                />
            );
        case ERRORS.ERROR_ITEM_QUANTITY_NOT_AVAILABLE:
            return (
                <FormattedMessage
                    id="checkout.CheckoutErrorMessage.ITEM_QUANTITY_NOT_AVAILABLE"
                    defaultMessage="We apologize, but this quantity is no longer available for sale. Please update your quantity selection."
                />
            );
        case ERRORS.ERROR_ITEM_NOT_AVAILABLE:
            return (
                <FormattedMessage
                    id="checkout.CheckoutErrorMessage.ITEM_NOT_AVAILABLE"
                    defaultMessage="We apologize, but this item is no longer available for sale. {continueShopping}."
                    values={{
                        // todo how does this link work?
                        continueShopping: (
                            <Link href="#" id="sold-continue">
                                <FormattedMessage
                                    id="checkout.CheckoutErrorMessage.continueShopping"
                                    defaultMessage="Continue shopping"
                                />
                            </Link>
                        ),
                    }}
                />
            );
        case ERRORS.ERROR_USER_CHANGED:
            return (
                <FormattedMessage
                    id="checkout.CheckoutErrorMessage.USER_CHANGED"
                    defaultMessage="There was a problem processing your order. We have now refreshed your account credentials in accordance with your most recent successful login. Please try submitting your order again. If the problem persists, <a>contact us</a>."
                    values={{ a: content => <ContactLink>{content}</ContactLink> }}
                />
            );
        case ERRORS.ERROR_DENIED:
            return (
                <span data-tn="credit-card-error-denied">
                    <FormattedMessage
                        id="checkout.CheckoutErrorMessage.DENIED1"
                        defaultMessage="Unfortunately we were unable to complete your payment at this time - sometimes this happens with larger purchases. Simply contact your credit card company or banking institution by calling the number on the back of your card to verify your payment. Once verified, submit your purchase a second time."
                    />
                    <br />
                    <FormattedMessage
                        id="checkout.CheckoutErrorMessage.DENIED2"
                        defaultMessage="If you receive an error message again, please <a>contact 1stDibs</a>. We are here to assist you with your order."
                        values={{ a: content => <ContactLink>{content}</ContactLink> }}
                    />
                </span>
            );
        case ERRORS.ERROR_VAULT:
            return (
                <span data-tn="credit-card-error-vault">
                    <FormattedMessage
                        id="checkout.CheckoutErrorMessage.VAULT1"
                        defaultMessage="Payment authorization failed."
                    />
                    <br />
                    <FormattedMessage
                        id="checkout.CheckoutErrorMessage.VAULT2"
                        defaultMessage="Please check your information and try again or contact your payment provider."
                    />
                    <br />
                    <span data-tn="checkout-error-support-info">
                        <FormattedMessage
                            id="checkout.CheckoutErrorMessage.VAULT3"
                            defaultMessage="For additional order assistance, please <a>contact 1stDibs</a>."
                            values={{
                                a: content => <ContactLink>{content}</ContactLink>,
                            }}
                        />
                    </span>
                </span>
            );
        case ERRORS.ERROR_EXPIRED:
            return (
                <FormattedMessage
                    id="checkout.CheckoutErrorMessage.EXPIRED"
                    defaultMessage="Your card has expired. Please add a new payment method below."
                />
            );
        case ERRORS.THREE_DS_REQUIRED:
            return (
                <FormattedMessage
                    id="checkout.CheckoutErrorMessage.THREE_DS_REQUIRED"
                    defaultMessage="We were unable to authorize your payment. Please try another form of payment."
                />
            );
        case ERRORS.ERROR_THREE_DS:
            return (
                <span data-tn="credit-card-error-3ds">
                    <FormattedMessage
                        id="checkout.CheckoutErrorMessage.3ds1"
                        defaultMessage="Payment authorization failed."
                    />
                    <br />
                    <FormattedMessage
                        id="checkout.CheckoutErrorMessage.3ds2"
                        defaultMessage="We were unable to authorize your credit card.  Please reach out to your credit card company or bank for help authorizing your credit card or try again with another form of payment.  "
                    />
                    <br />
                    <span data-tn="checkout-error-support-info">
                        <FormattedMessage
                            id="checkout.CheckoutErrorMessage.3ds3"
                            defaultMessage="For additional order assistance, please <a>contact 1stDibs</a>."
                            values={{
                                a: content => <ContactLink>{content}</ContactLink>,
                            }}
                        />
                    </span>
                </span>
            );
        case ERRORS.ACH_NON_US_COUNTRY:
            return (
                <span data-tn="ach-non-us-country">
                    <FormattedMessage
                        id="checkout.CheckoutErrorMessage.achNonUsCountry"
                        defaultMessage="In order to pay with your bank account, your billing address must be in the United States.  Please use a different billing address."
                    />
                    <br />
                    <span data-tn="checkout-error-support-info">
                        <FormattedMessage
                            id="checkout.CheckoutErrorMessage.achNonUsCountrySupportInfo"
                            defaultMessage="For additional order assistance, please <a>contact 1stDibs</a>."
                            values={{
                                a: content => <ContactLink>{content}</ContactLink>,
                            }}
                        />
                    </span>
                </span>
            );
        case ERRORS.ACH_APPROVED_WITH_RISK:
        case ERRORS.ACH_PROCESSOR_DECLINED:
        case ERRORS.ACH_DECLINED:
            return (
                <span data-tn="ach-declined">
                    <FormattedMessage
                        id="checkout.CheckoutErrorMessage.achDeclined"
                        defaultMessage="Payment authorization failed.  Please use a different payment method."
                    />
                    <br />
                    <span data-tn="checkout-error-support-info">
                        <FormattedMessage
                            id="checkout.CheckoutErrorMessage.achDeclinedSupportInfo"
                            defaultMessage="For additional order assistance, please <a>contact 1stDibs</a>."
                            values={{
                                a: content => <ContactLink>{content}</ContactLink>,
                            }}
                        />
                    </span>
                </span>
            );
        case ERRORS.ACH_INVALID_TRANSACTION_DATA:
        case ERRORS.ACH_NO_DATA_FOUND:
        case ERRORS.ACH_UNKNOWN_VERIFICATION_CODE:
            return (
                <span data-tn="ach-bad-data">
                    <FormattedMessage
                        id="checkout.CheckoutErrorMessage.achBadData"
                        defaultMessage="Payment authorization failed.  Please check your information and try again or use a different payment method."
                    />
                    <br />
                    <span data-tn="checkout-error-support-info">
                        <FormattedMessage
                            id="checkout.CheckoutErrorMessage.achBadDataSupportInfo"
                            defaultMessage="For additional order assistance, please <a>contact 1stDibs</a>."
                            values={{
                                a: content => <ContactLink>{content}</ContactLink>,
                            }}
                        />
                    </span>
                </span>
            );
        case ERRORS.SHIPPING_ERROR:
            return (
                <span data-tn="shipping-error">
                    <FormattedMessage
                        id="checkout.CheckoutErrorMessage.shippingError"
                        defaultMessage="There was an error setting your shipment preferences. Please make sure all of your information is correct and try again. If the problem persists, please contact us for assistance."
                    />
                    <br />
                </span>
            );
        case ERRORS.BRAINTREE_SANDBOX_ERROR:
            return (
                <span data-tn="braintree-sandbox-error">
                    <FormattedMessage
                        id="checkout.CheckoutErrorMessage.BRAINTREE_SANDBOX_ERROR"
                        defaultMessage="NOTE: This is in the Sandbox decline range of $2-3k. If you intended to make this checkout fail, ignore this error."
                    />
                </span>
            );
        case ERRORS.AUCTION_HAS_BID:
        case ERRORS.AUCTION_IS_PAUSED:
            return (
                <span data-tn="auction-has-bid-error">
                    <FormattedMessage
                        id="checkout.CheckoutErrorMessage.auctionError"
                        defaultMessage="This item is no longer available for purchase."
                    />
                    <br />
                </span>
            );
        case DENIED_PAYMENT_ERRORS.find(e => e === errorCode):
            return (
                <span data-tn={`denied-payment-authorization-${errorCode}`}>
                    <DeniedPaymentAuthorizationMessage
                        status={errorCode}
                        helpTextClass={styles.helperText}
                    />
                </span>
            );
        default:
            return (
                <span data-tn="checkout-error-message">
                    <FormattedMessage
                        id="checkout.CheckoutErrorMessage.default"
                        defaultMessage="There was an error processing your transaction. Please make sure all of your information is correct and try again. If the problem persists, please contact us for assistance."
                    />
                </span>
            );
    }
};

export default CheckoutErrorMessage;
